/**
 * Travel API Service
 * 여행 플래너 API 호출을 위한 서비스
 * JWT 인증 필요
 */

const API_BASE_URL = '/travel-api/api/v1';

/**
 * JWT 토큰 가져오기
 */
const getAuthToken = () => {
  return localStorage.getItem('jwtToken');
};

/**
 * API 요청 헤더 생성
 */
const getHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * API 응답 처리
 */
const handleResponse = async (response) => {
  // 401 Unauthorized - 토큰 만료 또는 유효하지 않음
  if (response.status === 401) {
    console.warn('⚠️ [TravelAPI] 401 Unauthorized - 로그아웃 처리');

    // 토큰 제거
    localStorage.removeItem('jwtToken');

    // 로그인 페이지로 리다이렉트
    window.location.href = '/login';

    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
  }

  const contentType = response.headers.get('content-type');

  if (contentType && contentType.includes('application/json')) {
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `API 오류: ${response.status}`);
    }

    return data;
  } else {
    throw new Error('Invalid response format');
  }
};

/**
 * API 호출 래퍼
 */
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    ...options,
    headers: {
      ...getHeaders(),
      ...options.headers,
    },
  };

  console.log(`🚀 [TravelAPI] ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, config);
    const result = await handleResponse(response);
    console.log(`✅ [TravelAPI] 성공:`, result);
    return result;
  } catch (error) {
    console.error(`❌ [TravelAPI] 오류:`, error);
    throw error;
  }
};

// ==========================================
// 여행 계획 API
// ==========================================

/**
 * 여행 계획 목록 조회
 */
export const getTravelPlans = async () => {
  return await apiCall('/travel-plans', {
    method: 'GET',
  });
};

/**
 * 특정 여행 계획 상세 조회
 */
export const getTravelPlan = async (planId) => {
  return await apiCall(`/travel-plans/${planId}`, {
    method: 'GET',
  });
};

/**
 * 새 여행 계획 생성
 */
export const createTravelPlan = async (planData) => {
  return await apiCall('/travel-plans', {
    method: 'POST',
    body: JSON.stringify(planData),
  });
};

/**
 * 여행 계획 수정
 */
export const updateTravelPlan = async (planId, planData) => {
  return await apiCall(`/travel-plans/${planId}`, {
    method: 'PUT',
    body: JSON.stringify(planData),
  });
};

/**
 * 여행 계획 삭제
 */
export const deleteTravelPlan = async (planId) => {
  return await apiCall(`/travel-plans/${planId}`, {
    method: 'DELETE',
  });
};

// ==========================================
// Day API
// ==========================================

/**
 * Day 시작 시간 업데이트
 */
export const updateDayStartTime = async (planId, dayId, startTime) => {
  return await apiCall(`/travel-plans/${planId}/days/${dayId}`, {
    method: 'PATCH',
    body: JSON.stringify({ start_time: startTime }),
  });
};

// ==========================================
// 참여자 API
// ==========================================

/**
 * 참여자 목록 조회
 */
export const getParticipants = async (planId) => {
  return await apiCall(`/travel-plans/${planId}/participants`, {
    method: 'GET',
  });
};

/**
 * 참여자 추가
 */
export const createParticipant = async (planId, participantData) => {
  return await apiCall(`/travel-plans/${planId}/participants`, {
    method: 'POST',
    body: JSON.stringify(participantData),
  });
};

/**
 * 참여자 수정
 */
export const updateParticipant = async (planId, participantId, participantData) => {
  return await apiCall(`/travel-plans/${planId}/participants/${participantId}`, {
    method: 'PUT',
    body: JSON.stringify(participantData),
  });
};

/**
 * 참여자 삭제
 */
export const deleteParticipant = async (planId, participantId) => {
  return await apiCall(`/travel-plans/${planId}/participants/${participantId}`, {
    method: 'DELETE',
  });
};

// ==========================================
// 일별 활동 API
// ==========================================

/**
 * 특정 날짜의 활동 목록 조회
 */
export const getActivities = async (planId, dayId) => {
  return await apiCall(`/travel-plans/${planId}/days/${dayId}/activities`, {
    method: 'GET',
  });
};

/**
 * 활동 추가
 */
export const createActivity = async (planId, dayId, activityData) => {
  return await apiCall(`/travel-plans/${planId}/days/${dayId}/activities`, {
    method: 'POST',
    body: JSON.stringify(activityData),
  });
};

/**
 * 활동 수정
 */
export const updateActivity = async (planId, dayId, activityId, activityData) => {
  return await apiCall(`/travel-plans/${planId}/days/${dayId}/activities/${activityId}`, {
    method: 'PUT',
    body: JSON.stringify(activityData),
  });
};

/**
 * 활동 삭제
 */
export const deleteActivity = async (planId, dayId, activityId) => {
  return await apiCall(`/travel-plans/${planId}/days/${dayId}/activities/${activityId}`, {
    method: 'DELETE',
  });
};

/**
 * 활동 순서 변경
 */
export const reorderActivities = async (planId, dayId, activityOrders) => {
  return await apiCall(`/travel-plans/${planId}/days/${dayId}/activities/reorder`, {
    method: 'PATCH',
    body: JSON.stringify({ activities: activityOrders }),
  });
};

// ==========================================
// 교통 정보 API
// ==========================================

/**
 * 교통 정보 목록 조회
 */
export const getTransportations = async (planId) => {
  return await apiCall(`/travel-plans/${planId}/transportations`, {
    method: 'GET',
  });
};

/**
 * 교통 정보 추가
 */
export const createTransportation = async (planId, transportData) => {
  return await apiCall(`/travel-plans/${planId}/transportations`, {
    method: 'POST',
    body: JSON.stringify(transportData),
  });
};

/**
 * 교통 정보 수정
 */
export const updateTransportation = async (planId, transportId, transportData) => {
  return await apiCall(`/travel-plans/${planId}/transportations/${transportId}`, {
    method: 'PUT',
    body: JSON.stringify(transportData),
  });
};

/**
 * 교통 정보 삭제
 */
export const deleteTransportation = async (planId, transportId) => {
  return await apiCall(`/travel-plans/${planId}/transportations/${transportId}`, {
    method: 'DELETE',
  });
};

// ==========================================
// 숙박 정보 API
// ==========================================

/**
 * 숙박 정보 목록 조회
 */
export const getAccommodations = async (planId) => {
  return await apiCall(`/travel-plans/${planId}/accommodations`, {
    method: 'GET',
  });
};

/**
 * 숙박 정보 추가
 */
export const createAccommodation = async (planId, accommodationData) => {
  return await apiCall(`/travel-plans/${planId}/accommodations`, {
    method: 'POST',
    body: JSON.stringify(accommodationData),
  });
};

/**
 * 숙박 정보 수정
 */
export const updateAccommodation = async (planId, accommodationId, accommodationData) => {
  return await apiCall(`/travel-plans/${planId}/accommodations/${accommodationId}`, {
    method: 'PUT',
    body: JSON.stringify(accommodationData),
  });
};

/**
 * 숙박 정보 삭제
 */
export const deleteAccommodation = async (planId, accommodationId) => {
  return await apiCall(`/travel-plans/${planId}/accommodations/${accommodationId}`, {
    method: 'DELETE',
  });
};

// ==========================================
// 체크리스트 API
// ==========================================

/**
 * 체크리스트 조회
 */
export const getChecklist = async (planId) => {
  return await apiCall(`/travel-plans/${planId}/checklist`, {
    method: 'GET',
  });
};

/**
 * 체크리스트 항목 추가
 */
export const createChecklistItem = async (planId, itemData) => {
  return await apiCall(`/travel-plans/${planId}/checklist`, {
    method: 'POST',
    body: JSON.stringify(itemData),
  });
};

/**
 * 체크리스트 완료 상태 토글
 */
export const toggleChecklistItem = async (planId, itemId) => {
  return await apiCall(`/travel-plans/${planId}/checklist/${itemId}/toggle`, {
    method: 'PATCH',
  });
};

/**
 * 체크리스트 항목 삭제
 */
export const deleteChecklistItem = async (planId, itemId) => {
  return await apiCall(`/travel-plans/${planId}/checklist/${itemId}`, {
    method: 'DELETE',
  });
};

/**
 * 체크리스트 순서 변경
 */
export const reorderChecklist = async (planId, itemOrders) => {
  return await apiCall(`/travel-plans/${planId}/checklist/reorder`, {
    method: 'PATCH',
    body: JSON.stringify({ items: itemOrders }),
  });
};

// ==========================================
// 공유 링크 API
// ==========================================

/**
 * 공유 링크 생성
 */
export const createShareLink = async (planId, shareData) => {
  return await apiCall(`/travel-plans/${planId}/share-link`, {
    method: 'POST',
    body: JSON.stringify(shareData),
  });
};

/**
 * 공유 링크 목록 조회
 */
export const getShareLinks = async (planId) => {
  return await apiCall(`/travel-plans/${planId}/share-links`, {
    method: 'GET',
  });
};

/**
 * 공유 링크 정보 조회 (인증 불필요)
 */
export const getShareLinkInfo = async (token) => {
  // 인증 불필요하므로 apiCall 대신 직접 fetch 사용
  const url = `${API_BASE_URL}/share/${token}`;
  console.log(`🚀 [TravelAPI] GET ${url}`);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    const result = await handleResponse(response);
    console.log(`✅ [TravelAPI] 성공:`, result);
    return result;
  } catch (error) {
    console.error(`❌ [TravelAPI] 오류:`, error);
    throw error;
  }
};

/**
 * 공유 링크 수락 (인증 필요)
 */
export const acceptShareLink = async (token) => {
  return await apiCall(`/share/${token}/accept`, {
    method: 'POST',
  });
};

/**
 * 공유 링크 삭제
 */
export const deleteShareLink = async (planId, token) => {
  return await apiCall(`/travel-plans/${planId}/share-links/${token}`, {
    method: 'DELETE',
  });
};

// 기본 export
const travelApiService = {
  // 여행 계획
  getTravelPlans,
  getTravelPlan,
  createTravelPlan,
  updateTravelPlan,
  deleteTravelPlan,

  // Day
  updateDayStartTime,

  // 참여자
  getParticipants,
  createParticipant,
  updateParticipant,
  deleteParticipant,

  // 활동
  getActivities,
  createActivity,
  updateActivity,
  deleteActivity,
  reorderActivities,

  // 교통
  getTransportations,
  createTransportation,
  updateTransportation,
  deleteTransportation,

  // 숙박
  getAccommodations,
  createAccommodation,
  updateAccommodation,
  deleteAccommodation,

  // 체크리스트
  getChecklist,
  createChecklistItem,
  toggleChecklistItem,
  deleteChecklistItem,
  reorderChecklist,

  // 공유 링크
  createShareLink,
  getShareLinks,
  getShareLinkInfo,
  acceptShareLink,
  deleteShareLink,
};

export default travelApiService;
